import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar } from '@/components/core/linked-record-bar/linked-record-bar';
import { useLinkedRecord, useOpenLinkedRecord } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { DEFAULT_APPLICATIONS_ORDER } from '../application-api';
import { PagingResult } from '../service-desk.types';

const PAGE_SIZE = 15;

/**
 * Paging, sorting and column filters of a service desk list, all run on the server (newest
 * application first until a column is sorted). Send `order` and `mergeWhere(filter, columnWhere)`
 * with the list query; `resetPage` goes back to the first page when a toolbar filter changes. A
 * notification's link (useLinkedRecord) lists only its application.
 */
export const useApplicationsTable = (initialPageSize = PAGE_SIZE) => {
  const { linkedId, clearLinked } = useLinkedRecord();
  const table = useServerTable({
    pageSize: initialPageSize,
    defaultOrder: DEFAULT_APPLICATIONS_ORDER,
    linkedId,
  });
  return { ...table, linkedId, clearLinked };
};

export type ApplicationsTableState = ReturnType<typeof useApplicationsTable>;

interface ApplicationsTableProps<TItem> {
  columns: ColumnDef<TItem>[];
  result: PagingResult<TItem> | undefined;
  isLoading: boolean;
  error: unknown;
  table: ApplicationsTableState;
  onRowClick?: (item: TItem) => void;
  /** On a phone, actions on the cards' filter line (e.g. the CSV download), left of the filter. */
  mobileActions?: React.ReactNode;
  /** The table toolbar (e.g. a queue status switch). */
  toolbar?: React.ReactNode;
}

const NO_ITEMS: never[] = [];
const NO_ROW_CLICK = () => undefined;

/**
 * The AdvanceDataTable configuration every service desk list uses (server paging/sorting/filters, no
 * expansion). A linked application opens as a click on its row would (its details, where there are any).
 */
export const ApplicationsTable = <TItem extends { ItemId: string }>({
  columns,
  result,
  isLoading,
  error,
  table,
  onRowClick,
  mobileActions,
  toolbar,
}: ApplicationsTableProps<TItem>) => {
  const items = result?.items ?? NO_ITEMS;
  useOpenLinkedRecord(table.linkedId, items, onRowClick ?? NO_ROW_CLICK);

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={table.linkedId} onShowAll={table.clearLinked} />
      <AdvanceDataTable
        {...table.tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{ ...table.pagination, totalCount: result?.totalCount ?? 0 }}
        isExpandRowContent={false}
        tableClassName="min-w-[56rem]"
        onRowClick={onRowClick}
        // On a phone each application is a card; a column's `meta.mobile` places it.
        mobileView="cards"
        mobileActions={mobileActions}
        columnsToolbar={toolbar ? () => toolbar : undefined}
      />
    </div>
  );
};
